import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  AMENDMENT_DIALOG_CONFIG,
  AmendmentDialog,
  AmendmentDialogData,
} from '@app/features/amendments/amendment-dialog/amendment-dialog';
import { Amendment } from '@app/features/amendments/amendment.model';
import { AmendmentService } from '@app/features/amendments/amendment.service';
import { ContractService } from '@app/features/contracts/contract-service';
import { Contract, ContractListItem } from '@app/features/contracts/contract.model';
import { toLocalDateString } from '@app/shared/date-utils';
import moment from 'moment';
import { EMPTY, forkJoin, from, Observable, of } from 'rxjs';
import { concatMap, filter, map, switchMap, take, toArray } from 'rxjs/operators';

/**
 * Ouvre l'établissement d'un avenant sans avoir à retrouver le contrat : depuis la liste des
 * contrats, ou depuis le salarié. L'API dit seule si l'avenant est possible ; sinon, la raison
 * s'affiche.
 */
@Injectable({ providedIn: 'root' })
export class AmendmentLauncher {
  private readonly amendmentService = inject(AmendmentService);
  private readonly contractService = inject(ContractService);
  private readonly dialog = inject(MatDialog);
  private readonly snackbar = inject(MatSnackBar);

  /**
   * Un contrat de la liste peut-il recevoir un avenant, à ce qu'elle en dit : un CDI ou un CDD A ni
   * annulé ni supprimé, dont le terme n'est pas passé, sans avenant en cours. Un contrat pas encore
   * signé, ou pas encore commencé, en reçoit un par anticipation. L'API tranche au clic.
   */
  static mayReceiveAmendment(contract: ContractListItem, today = moment()): boolean {
    const endDate = toLocalDateString(contract.endDate);
    const day = today.format('YYYY-MM-DD');
    return (
      !contract.deleted &&
      !contract.amendmentInProgress &&
      contract.status !== 'CANCELED' &&
      (contract.contractType === 'CDI' || contract.contractType === 'CDD A') &&
      (endDate === null || endDate >= day)
    );
  }

  /** Ouvre la saisie d'un avenant sur ce contrat, ou dit pourquoi elle n'est pas possible. */
  establishOnContract(contractId: string): Observable<MatDialogRef<AmendmentDialog>> {
    return this.amendmentService.eligibility(contractId).pipe(
      switchMap((eligibility) => {
        if (!eligibility.allowed) {
          this.refuse(eligibility.refusal ?? 'Ce contrat ne peut pas recevoir d’avenant.');
          return EMPTY;
        }
        return this.contractService
          .findOne(contractId)
          .pipe(map((contract) => this.open(contract)));
      }),
    );
  }

  /**
   * Ouvre la saisie d'un avenant sur le contrat en cours du salarié : le plus récent de ses
   * contrats que l'API déclare éligible.
   */
  establishForEmployee(employeeId: string): Observable<MatDialogRef<AmendmentDialog>> {
    return this.contractService.mostRecentsByEmployee(employeeId, null).pipe(
      map((contracts) =>
        contracts
          .filter((contract) => AmendmentLauncher.mayReceiveAmendment(contract))
          .sort((a, b) => (b.startDate ?? '').localeCompare(a.startDate ?? '')),
      ),
      switchMap((candidates) =>
        from(candidates).pipe(
          concatMap((candidate) =>
            this.amendmentService
              .eligibility(candidate.id)
              .pipe(map((eligibility) => ({ candidate, eligibility }))),
          ),
          toArray(),
        ),
      ),
      switchMap((checked) => {
        const eligible = checked.find(({ eligibility }) => eligibility.allowed);
        if (eligible) {
          return of(eligible.candidate.id);
        }
        this.refuse(
          checked[0]?.eligibility.refusal ??
            'Ce salarié n’a aucun CDI ni CDD A en cours ou à venir : un avenant ne peut pas s’établir.',
        );
        return EMPTY;
      }),
      take(1),
      switchMap((contractId) => this.contractService.findOne(contractId)),
      filter((contract): contract is Contract => !!contract),
      map((contract) => this.open(contract)),
    );
  }

  /** Ouvre la consultation d'un avenant, sans passer par son contrat. */
  consult(contractId: string, amendmentId: string): Observable<MatDialogRef<AmendmentDialog>> {
    return forkJoin([
      this.contractService.findOne(contractId),
      this.amendmentService.findOne(amendmentId),
    ]).pipe(map(([contract, amendment]) => this.open(contract, amendment)));
  }

  private open(contract: Contract, amendment?: Amendment): MatDialogRef<AmendmentDialog> {
    return this.dialog.open<AmendmentDialog, AmendmentDialogData>(AmendmentDialog, {
      data: amendment ? { contract, amendment } : { contract },
      ...AMENDMENT_DIALOG_CONFIG,
    });
  }

  private refuse(reason: string): void {
    this.snackbar.open(reason, 'Fermer', { duration: 8000 });
  }
}
